import React, {memo} from "react";
import {get} from "lodash";
import {UserPermissionEnum} from "../../../utils/types/user";

import {BarChart} from "../../../atoms/admin-reporting/atoms/bar-chart";
import {
  AggregationType,
  AnalyticsType,
  ReportingContextType,
} from "../../../store/reporting/reporting.common";
import {RequiredUserPermission} from "../../../atoms/admin-user-permissions/RequiredUserPermission";
import {ReportingContext} from "../ReportingContext";
import {CompareDateLabel} from "../../../atoms/reporting-compare";
import {CardTitle} from "../../../atoms/card-title";
import {
  ChartProps,
  Props,
  useOnDataPathChange,
  dataProcess_AnalyticsPercentageByFacility,
  numberToPercent,
  withCompare,
} from "./helpers";
import {CompareDateLabelsWrapper} from "../reports/Report.common";

const AnalyticsWithoutMedicalSupplementByFacilityChart = memo(
  ({data, isFullReport}: ChartProps) => {
    const barData = dataProcess_AnalyticsPercentageByFacility(data);

    return (
      <BarChart
        data={barData.stack}
        max={barData.max}
        labelLeft="Facility"
        labelRight="Percentage"
        full={isFullReport}
        itemsPerRow={1}
        dataLegend={[
          {name: "Residents without Medicare"},
          {name: "Residents with Medicare"},
        ]}
        axTickFormat={v => numberToPercent(v)}
        hoverDataPreprocessor={(data, line, index) => {
          const sum = get(
            line.original[index].aggregations,
            AggregationType.SUM,
            0,
          );
          const count = get(
            line.original[index].aggregations,
            AggregationType.COUNT,
            0,
          );

          return [sum, count - sum];
        }}
      />
    );
  },
);

export const WITHOUT_MEDICARE_SUPPLEMENT_BY_FACILITY_CHART_ID =
  "without-medicare-supplement-by-facility" as const;

export const AnalyticsWithoutMedicalSupplementByFacility = (props: Props) => (
  <RequiredUserPermission
    permission={UserPermissionEnum.PATIENTS_WITHOUT_MEDICAL_SUPPLEMENT}
  >
    <div className="chart-header">
      <CardTitle title="Resident without Medicare" subtitle="by Facility" />
      <CompareDateLabelsWrapper>
        <CompareDateLabel dateRange={props.dateRange} global />
      </CompareDateLabelsWrapper>
    </div>
    <ReportingContext
      dataPaths={withCompare({
        what: ReportingContextType.analyticsWithoutResolution,
        type: AnalyticsType.missingMedicareRateByFacility,
        query: {...props.dateRange},
        facilities: props.facilities,
      })}
      onDataPathChange={useOnDataPathChange(props.reportingBlockSetters)}
      isFullReport={props.reportingBlockSetters.isFullReport}
      enabled={props.reportingBlockSetters.enabled}
    >
      {(data, isFullReport) => (
        <AnalyticsWithoutMedicalSupplementByFacilityChart
          data={data}
          isFullReport={isFullReport}
        />
      )}
    </ReportingContext>
  </RequiredUserPermission>
);
